/** One versioned bootstrap read per authenticated tenancy; shared by both observers. */
import { useQuery } from "@tanstack/react-query";
import { useFeaturesSource, usePoliciesSource } from "@/api/adapters";
import { useStorageIdentity } from "./storageIdentity";

export function useConfigSnapshot(passive = false) {
  const identity = useStorageIdentity();
  const features = useFeaturesSource();
  const policies = usePoliciesSource();
  return useQuery({
    queryKey: ["config-snapshot", identity.customerId, identity.orgId, identity.tenantId, identity.userId],
    queryFn: async ({ signal }) => {
      if (features.getConfigSnapshot) {
        const snapshot = await features.getConfigSnapshot(signal);
        if (!snapshot.readable) throw new Error("Workspace configuration is temporarily unavailable");
        return snapshot;
      }
      const [flags, policy] = await Promise.all([features.getFeatureFlags(signal), policies.getPolicies(signal)]);
      return { features: flags, policies: policy, version: null, readable: true };
    },
    enabled: Boolean(identity.customerId || identity.orgId || identity.tenantId),
    staleTime: 60_000,
    refetchInterval: passive ? false : 60_000,
    refetchOnWindowFocus: !passive,
    retry: 1,
  });
}
